<template>
  <div class="social-media-dialog text-center">
    <!-- TODO: Change width to 1100 on preview step-->
    <v-dialog
      v-model="dialog"
      :width="isMasterView && step === 3 ? '80vw' : 500"
      :fullscreen="$vuetify.display.smAndDown"
      content-class="social-media-dialog"
      :persistent="step !== (isMasterView ? 4 : 3)"
      data-testid="content-creator-social-sharing-dialog"
    >
      <template v-slot:activator="{ props: dialogProps }">
        <v-tooltip
          :disabled="!disabled"
          location="top"
        >
          <template v-slot:activator="{ props: tooltipProps }">
            <slot
              name="button"
              :props="{ ...dialogProps, ...tooltipProps }"
            >
              <div
                v-bind="tooltipProps"
                v-if="isPostButtonVisible"
              >
                <UpgradeInfoDialog :show-info="showVideoUpsell">
                  <template v-slot="{ props: upgradeProps }">
                    <v-btn
                      v-bind="showVideoUpsell ? upgradeProps : dialogProps"
                      variant="outlined"
                      color="primary"
                      class="ma-2 button"
                      :disabled="disabled"
                      data-product-fruits="content-creator-schedule-post"
                      data-testid="content-creator-schedule-btn"
                      @click="!showVideoUpsell && (isScheduledPostPreselected = true)"
                    >
                      <v-icon
                        class="mr-1"
                        size="16"
                      >
                        mdi-calendar-month-outline
                      </v-icon>
                      {{ $t('content-creator.schedule-post.button') }}
                    </v-btn>
                  </template>
                </UpgradeInfoDialog>
                <UpgradeInfoDialog :show-info="showVideoUpsell">
                  <template v-slot="{ props: upgradeProps }">
                    <v-btn
                      color="primary"
                      class="button"
                      v-bind="showVideoUpsell ? upgradeProps : dialogProps"
                      :disabled="disabled"
                      data-product-fruits="content-creator-post-now"
                      data-testid="content-creator-post-now-btn"
                      @click="!showVideoUpsell && (isScheduledPostPreselected = false)"
                    >
                      <v-icon
                        class="mr-1"
                        size="16"
                      >
                        mdi-share-variant
                      </v-icon>
                      {{ $t('content-creator.post.button') }}
                    </v-btn>
                  </template>
                </UpgradeInfoDialog>
              </div>
            </slot>
          </template>
          <span>{{ $t('buttons.content-creator.tooltip.disable-post') }}</span>
        </v-tooltip>
      </template>

      <v-card class="social-media-dialog d-flex flex-column">
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title
              v-if="isScheduledPost"
              class="dialog-title text-white"
            >
              {{ $t('content-creator.reschedule.title') }}
            </v-toolbar-title>
            <v-toolbar-title
              v-else
              class="dialog-title text-white"
            >
              {{ $t('content-creator.post.title') }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                @click="dialog = false"
              >
                <v-icon
                  size="large"
                  class="text-white"
                >
                  mdi-close
                </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-overlay
          v-if="isPolling"
          :model-value="isPolling"
          contained
          scrim="#ffffff"
          :opacity="1"
          class="align-center justify-center"
          style="z-index: 10"
        >
          <div class="d-flex flex-column align-center ga-4 pa-8 text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            />
            <div class="text-subtitle-1">
              {{ $t('social-media.posting.in-progress') }}
            </div>
            <div
              v-if="postingStatus && isMasterView"
              class="text-body-2 text-medium-emphasis"
            >
              {{ postingStatus.completed }} / {{ postingStatus.total }}
              {{ $t('social-media.posting.progress') }}
            </div>
            <div
              v-else-if="postingStatus && postingStatus.platforms.length"
              class="d-flex flex-column align-center ga-1 text-medium-emphasis"
            >
              <div
                v-for="p in postingStatus.platforms"
                :key="p.platform"
                class="d-flex flex-column align-center ga-1"
              >
                <div class="d-flex align-center ga-2 text-body-2">
                  <span>{{ platformLabel(p.platform) }}</span>
                  <v-icon
                    v-if="p.status === 'COMPLETED'"
                    color="success"
                    size="16"
                    >mdi-check-circle</v-icon
                  >
                  <v-icon
                    v-else-if="p.hasFailed"
                    color="error"
                    size="16"
                    >mdi-close-circle</v-icon
                  >
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="16"
                    width="2"
                  />
                </div>
                <div
                  v-if="p.hasFailed && p.failureReason"
                  class="text-caption text-error text-center"
                  style="max-width: 280px"
                >
                  {{ p.failureReason }}
                </div>
              </div>
            </div>
            <div
              v-if="showHistoryHint"
              class="d-flex flex-column align-center ga-1 mt-2"
            >
              <div class="text-body-2 text-center text-medium-emphasis">
                {{ $t('social-media.posting.hint-check-history') }}
              </div>
              <v-btn
                variant="text"
                size="small"
                @click="dismissPollingAndClose"
              >
                {{ $t('labels.close') }}
              </v-btn>
            </div>
          </div>
        </v-overlay>

        <v-stepper
          v-if="dialog"
          v-model="step"
          alt-labels
          flat
          color="primary"
          elevation="0"
          class="overflow-y-auto"
        >
          <v-stepper-window v-model="step">
            <v-stepper-window-item
              :value="0"
              class="py-0"
            >
              <ImageLicenseStep
                v-if="step === 0"
                :imageSourceVariant="selectedImageSourceVariant"
                :stockPhotoId="localContent.stockPhotoId"
                @imageUrl="handleImageLicense"
                @error="dialog = false"
              />
            </v-stepper-window-item>
            <v-stepper-window-item
              :value="1"
              class="py-0"
            >
              <SharingStep
                :text-kind="localContent.textKind"
                :selected-platforms="selectedPlatforms"
                :is-master-view="isMasterView"
                :can-post-to-instagram="canPostToInstagram"
                :is-mov-video="isMovVideo"
                @next="handleSharingStepNext"
                @selectedPages="selectedPages = $event"
              />
            </v-stepper-window-item>

            <v-stepper-window-item
              :value="2"
              class="py-0"
            >
              <ScheduleStep
                :pages="selectedPages"
                :content="localContent"
                :is-update-for-scheduled-post="isUpdateForScheduledPost"
                :is-scheduled-post-preselected="isScheduledPostPreselected"
                :is-posting="isLoading"
                @close="dialog = false"
                @back="step = 1"
                @success="handleSuccess"
                @updatePostSuccess="handleUpdatePostSuccess"
                @isScheduledPost="isScheduledPost = $event"
                @scheduleSelected="handleScheduleSelected"
              />
            </v-stepper-window-item>
            <v-stepper-window-item
              v-if="isMasterView"
              :value="3"
              class="py-0"
            >
              <PreviewStep
                :content="previewContent"
                :offices="previewOffices"
                :show-post-column="false"
                :submit-text="
                  isScheduledPost
                    ? $t('content-creator.schedule-post.button')
                    : $t('content-creator.post.button')
                "
                :submit-icon="isScheduledPost ? 'mdi-calendar-check' : 'mdi-publish'"
                :show-back-button="true"
                :is-posting="isLoading"
                :disabled="!canPost"
                @back="step = 2"
                @submit="handlePreviewSubmit"
              />
            </v-stepper-window-item>
            <v-stepper-window-item :value="isMasterView ? 4 : 3">
              <SuccessStep
                :is-scheduled-post="isScheduledPost"
                :posts="posts"
                :is-master-view="isMasterView"
                :is-update-for-scheduled-post="isUpdateForScheduledPost"
                @close="dialog = false"
              >
                <template
                  #default
                  v-if="isMasterView"
                >
                  <SuccessStepMasterContent
                    :is-scheduled-post="isScheduledPost"
                    :posts="posts"
                    :platforms="selectedPages.map((page) => page.type)"
                    :offices="previewOffices.length"
                    :accounts="socialMediaAccounts"
                    :summary="masterBatchSummary"
                    :scheduled-date="scheduleData?.date"
                  />
                </template>
                <template
                  #actions
                  v-if="isMasterView"
                >
                  <div class="d-flex justify-center w-full gap-1">
                    <v-btn
                      variant="outlined"
                      @click="goToFirstStep"
                      >{{
                        isScheduledPost
                          ? $t('content-creator.schedule-on-other-platforms')
                          : $t('content-creator.share-on-other-platforms')
                      }}</v-btn
                    >
                    <v-btn
                      variant="flat"
                      @click="dialog = false"
                    >
                      {{ $t('buttons.finish') }}
                    </v-btn>
                  </div>
                </template>
              </SuccessStep>
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showPlaceholderWarning"
      max-width="500"
    >
      <v-card>
        <v-toolbar color="warning">
          <v-toolbar-title>
            {{ $t('content-creator.placeholders.warning-title') }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-title>
          {{ $t('content-creator.placeholders.warning-title') }}
        </v-card-title>
        <v-card-text>
          <p class="mb-0">{{ $t('content-creator.placeholders.warning-text') }}</p>
          <ul class="mt-2 ml-6">
            <li
              v-for="office in placeholderWarningOffices"
              :key="office.id || office.name"
            >
              {{ office.name }}
            </li>
          </ul>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            @click="showPlaceholderWarning = false"
          >
            {{ $t('buttons.content-creator.close') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="confirmPlaceholderWarning"
          >
            {{ $t('content-creator.placeholders.post-anyway') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { computed } from 'vue'

import { useImageSentry } from '@/components/image-edit/composables/useImageSentry'
import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import ImageType from '@/lib/enums/ImageType'
import { cropImageToAspectRatio } from '@/lib/imageCropping'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import feature from '@/mixins/feature'
import GET_CHILD_COMPANIES from '@/modules/company/queries/ChildCompanies.gql'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType'
import {
  GoogleBusinessConnectionStatus,
  ReadableSocialMedia,
  SocialMedia,
} from '@/modules/contentCreator/creator/enums/SocialMedia'
import { TargetLink } from '@/modules/contentCreator/creator/enums/targetLink'

import { ContentType } from '../enums/ContentType'
import KindType from '../enums/KindType'
import { getOfficesWithMissingPlaceholders } from '../lib/placeholders.js'
import { summarizeBatchPosts } from '../lib/publishingStatus.js'
import ratioMap from '../lib/ratioMap.js'
import { buildUrlWithTracking } from '../lib/urlWithTracking'
import EDIT_IMAGE from '../queries/editImage.gql'
import GET_COMPANIES_POSTING_INFO from '../queries/getCompaniesPostingInfo.gql'
import GET_MASTER_BATCH_POSTS from '../queries/getMasterBatchPosts.gql'
import POST_TO_SOCIAL_MEDIA_FOR_COMPANIES from '../queries/postToSocialMediaForCompanies.gql'
import PREPARE_STOCK_IMAGE_FOR_EDIT from '../queries/prepareStockImageForEdit.gql'
import SOCIAL_MEDIA_POSTING_STATUS from '../queries/socialMediaPostingStatus.gql'
import ImageLicenseStep from './ImageLicenseStep.vue'
import PreviewStep from './PreviewStep.vue'
import ScheduleStep from './ScheduleStep.vue'
import SharingStep from './SharingStep.vue'
import SuccessStep from './SuccessStep.vue'
import SuccessStepMasterContent from './SuccessStepMasterContent.vue'

export default {
  components: {
    ImageLicenseStep,
    SharingStep,
    ScheduleStep,
    PreviewStep,
    SuccessStep,
    SuccessStepMasterContent,
    UpgradeInfoDialog,
  },
  mixins: [feature],
  inject: ['companyBaseData'],
  provide() {
    return {
      selectedCarouselImages: computed(() => this.localContent.carouselImages || []),
      supportCarouselPosts: computed(() => [KindType.facebookPost, KindType.instagramPost]),
    }
  },
  props: {
    content: {
      type: Object,
      default: () => ({}),
    },
    contentType: {
      type: String,
      default: '',
    },
    isUpdate: {
      type: Boolean,
      default: false,
    },
    isLicenseRequired: {
      type: Boolean,
      default: false,
    },
    selectedImageSourceVariant: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    companyIds: {
      type: Array,
      default: () => [],
    },
    selectedOffices: {
      type: Array,
      default: () => [],
    },
    publication: {
      type: String,
      default: '',
    },
    includeHeadOffice: {
      type: Boolean,
      default: false,
    },
    canPostToInstagram: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isScheduledPostPreselected: true,
      isScheduledPost: this.contentType === ContentType.SCHEDULED_CONTENT,
      selectedPages: [],
      posts: [],
      dialog: false,
      step: this.isLicenseRequired ? 0 : 1,
      isLoading: false,
      localContent: { ...this.content },
      scheduleData: null,
      sentry: null,
      isPolling: false,
      postingStatus: null,
      postingBatchId: null,
      masterBatchSummary: null,
      showHistoryHint: false,
      showPlaceholderWarning: false,
      placeholderWarningOffices: [],
    }
  },
  computed: {
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    isNewsletterTextKind() {
      return this.localContent.textKind === PostType.newsletter
    },
    isPostButtonVisible() {
      const postTypes = [
        PostType.facebookPost,
        PostType.instagramPost,
        PostType.linkedinPost,
        PostType.gmbNewsPost,
        PostType.gmbProductDescription,
      ]
      return postTypes?.includes(this.localContent.textKind)
    },
    isPostVideoEnabled() {
      return this.getFeature(this.featureNames.CONTENT_CREATOR).config.postVideo
    },
    excludedCompanyIdsFromPosting() {
      return (
        this.getFeature(this.featureNames.CONTENT_CREATOR)?.config?.excludedCompanyIdsFromPosting ||
        []
      )
    },
    showVideoUpsell() {
      return this.content.isVideo && !this.isPostVideoEnabled
    },
    // LinkedIn does not accept MOV (QuickTime) video files, so posting them must be blocked.
    isMovVideo() {
      if (!this.localContent.isVideo) return false
      const url = this.localContent.image || this.localContent.imagePath || ''
      return this.localContent.mediaType === 'video/quicktime' || /\.mov(\?|$)/i.test(url)
    },
    selectedPlatforms() {
      return this.localContent?.socialMediaPosts?.map(({ type }) => type) || []
    },
    isUpdateForScheduledPost() {
      return this.isUpdate && this.contentType === ContentType.SCHEDULED_CONTENT
    },
    isUpdateForSavedContent() {
      return this.isUpdate && this.contentType === ContentType.SAVED_CONTENT
    },
    targetLink() {
      return this.localContent.targetLink?.name
    },
    individualLink() {
      return this.localContent.targetLink?.custom || ''
    },
    previewContent() {
      return {
        topicName: this.localContent.topicName,
        image: this.localContent.image || this.localContent.imagePath,
        text: this.localContent.text,
        date: this.isScheduledPost
          ? this.scheduleData?.date ||
            this.localContent.socialMediaPosts?.[0]?.scheduledAt ||
            this.localContent.scheduledAt
          : null,
      }
    },
    previewOffices() {
      if (!this.isMasterView) {
        return []
      }

      const createName = (companyId, fallbackName = '') => {
        const company = this.getCompaniesPostingInfo?.find((c) => c.id === companyId)
        if (!company) return fallbackName
        const { firstname = '', lastname = '' } = company?.ambassador || {}
        const ambassadorName = [firstname, lastname].filter(Boolean).join(' ')
        const { city = '' } = company?.address || {}
        const officeName = company?.name || companyId
        return [officeName, ambassadorName, city].filter(Boolean).join(' - ')
      }

      // When rescheduling scheduled content, use existing posts data
      if (
        this.isUpdateForScheduledPost &&
        this.localContent.posts &&
        this.localContent.posts.length > 0
      ) {
        const posts = this.localContent.posts || []
        const uniqueCompanyIds = [...new Set(posts.map((post) => post.companyId).filter(Boolean))]

        return uniqueCompanyIds.map((companyId) => {
          // Aggregate all posts for this company (not just the first one)
          const postsForCompany = posts.filter((p) => p.companyId === companyId)
          // Combine all social media posts from all posts for this company
          const socialMediaPosts = postsForCompany.flatMap((post) => post.socialMediaPosts || [])
          const uniqueTypes = [...new Set(socialMediaPosts.map((sm) => sm.type).filter(Boolean))]
          const socialMediaAccounts = uniqueTypes
            .map((type) => this.toPreviewAccount(companyId, type))
            .filter(Boolean)

          // Use the first post for company name and URL (they should be the same across posts)
          const firstPost = postsForCompany[0]
          return {
            name: createName(companyId, firstPost?.companyName || companyId),
            socialMedia: socialMediaAccounts.length > 0 ? socialMediaAccounts : null,
            targetLink: firstPost?.url || this.localContent.url || '',
            postUrl: '',
          }
        })
      }

      // When sharing saved content, use existing posts data
      if (
        this.isUpdateForSavedContent &&
        this.localContent.posts &&
        this.localContent.posts.length > 0
      ) {
        const posts = this.localContent.posts || []
        const uniqueCompanyIds = [...new Set(posts.map((post) => post.companyId).filter(Boolean))]

        return uniqueCompanyIds.map((companyId) => {
          // Aggregate all posts for this company (not just the first one)
          const postsForCompany = posts.filter((p) => p.companyId === companyId)
          // Combine all social media posts from all posts for this company
          const socialMediaPosts = postsForCompany.flatMap((post) => post.socialMediaPosts || [])
          const uniqueTypes = [...new Set(socialMediaPosts.map((sm) => sm.type).filter(Boolean))]
          const socialMediaAccounts = uniqueTypes
            .map((type) => this.toPreviewAccount(companyId, type))
            .filter(Boolean)

          // Use the first post for company name and URL (they should be the same across posts)
          const firstPost = postsForCompany[0]
          return {
            name: createName(companyId, firstPost?.companyName || companyId),
            socialMedia: socialMediaAccounts.length > 0 ? socialMediaAccounts : null,
            targetLink: firstPost?.url || this.localContent.url || '',
            postUrl: '',
          }
        })
      }

      let companyIdsToShow = []
      if (this.includeHeadOffice && this.publication !== 'all')
        companyIdsToShow.push(this.$auth.user.companyId)
      if (this.selectedOffices.length > 0)
        companyIdsToShow.push(...this.selectedOffices.map((office) => office.id))
      if (this.publication === 'all') {
        companyIdsToShow.push(...(this.childCompanies || []).map((company) => company.id))

        if (this.excludedCompanyIdsFromPosting.length > 0 && this.isMasterView) {
          companyIdsToShow = companyIdsToShow.filter(
            (companyId) => !this.excludedCompanyIdsFromPosting.includes(companyId?.toString())
          )
        }
      }

      if (!companyIdsToShow || companyIdsToShow.length === 0) {
        return []
      }

      const childCompanies = this.getCompaniesPostingInfo || []
      const platformsToPost =
        this.selectedPages && this.selectedPages.length > 0
          ? this.selectedPages.map((page) => (page.type || '').toUpperCase())
          : (this.selectedPlatforms || []).map((type) => (type || '').toUpperCase())

      if (!platformsToPost || platformsToPost.length === 0) {
        return []
      }

      const companyMap = {}

      const masterCompanyId = this.$auth?.user?.companyId
      if (masterCompanyId && this.companyBaseData?.name) {
        companyMap[masterCompanyId] = {
          name: this.companyBaseData.name,
          ambassadorImage: this.companyBaseData?.ambassador?.image?.url || null,
        }
      }

      childCompanies.forEach((company) => {
        if (company && company.id) {
          companyMap[company.id] = {
            name: company.name || company.id,
            ambassadorImage: company?.ambassador?.image?.url || null,
          }
        }
      })

      return companyIdsToShow
        .map((companyId) => {
          const company = companyMap[companyId]
          if (!company) return null

          const targetLinkUrl = this.getTargetLinkUrl(companyId)
          const socialMediaAccounts = this.selectedPages
            .map((page) => this.toPreviewAccount(companyId, page.type))
            .filter(Boolean)

          return {
            name: createName(companyId),
            socialMedia: socialMediaAccounts.length > 0 ? socialMediaAccounts : null,
            targetLink: buildUrlWithTracking(
              targetLinkUrl,
              this.localContent.textKind,
              this.localContent.imageSource
            ),
            postUrl: '',
            extras: {
              ambassadorImage: company.ambassadorImage,
              isPersonalizedModel: this.localContent.modelName === 'AI-gemini-flash-personalized',
            },
          }
        })
        .filter(Boolean)
    },

    socialMediaAccounts() {
      return this.previewOffices.filter((office) => {
        if (Array.isArray(office.socialMedia)) {
          return office.socialMedia.length > 0
        }
        return office.socialMedia
      }).length
    },
    canPost() {
      if (!this.isMasterView) return true

      return this.previewOffices.some((office) => {
        if (Array.isArray(office.socialMedia)) {
          return office.socialMedia.some((account) => account.isConnected)
        }
        return office.socialMedia
      })
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        // Sync localContent with the latest content prop when dialog opens
        this.localContent = { ...this.content }
        this.resetState()
        if (this.isUpdateForScheduledPost && this.localContent.socialMediaPosts) {
          // Initialize selectedPages from existing socialMediaPosts when rescheduling
          this.selectedPages = this.localContent.socialMediaPosts.map((post) => ({
            type: post.type,
            id: post.postId || post.id,
            name: post.type,
            selected: true,
          }))
        }
        if (this.isUpdateForSavedContent) {
          // Initialize selectedPages from existing socialMediaPosts or posts when sharing saved content
          if (this.localContent.socialMediaPosts && this.localContent.socialMediaPosts.length > 0) {
            // Use top-level socialMediaPosts if available
            this.selectedPages = this.localContent.socialMediaPosts.map((post) => ({
              type: post.type,
              id: post.postId || post.id || post.externalPostId,
              name: post.type,
              selected: true,
            }))
          } else if (this.localContent.posts && this.localContent.posts.length > 0) {
            // Otherwise use socialMediaPosts from posts array
            const socialMediaPosts = this.localContent.posts.flatMap(
              (post) => post.socialMediaPosts || []
            )
            const uniqueTypes = [
              ...new Set(socialMediaPosts.map((post) => post.type).filter(Boolean)),
            ]
            this.selectedPages = uniqueTypes.map((type) => {
              const post = socialMediaPosts.find((p) => p.type === type)
              return {
                type: type,
                id: post?.postId || post?.id || post?.externalPostId,
                name: type,
                selected: true,
              }
            })
          }
        }
        // Refetch companies posting info when dialog opens for saved/scheduled content
        if (
          this.isMasterView &&
          this.localContent?.topicId &&
          (this.isUpdateForScheduledPost || this.isUpdateForSavedContent) &&
          this.$apollo.queries.getCompaniesPostingInfo
        ) {
          this.$nextTick(() => {
            this.$apollo.queries.getCompaniesPostingInfo.refetch()
          })
        }
      }
    },
    content: {
      handler() {
        this.localContent = { ...this.content }
      },
      deep: true,
    },
    isLicenseRequired() {
      this.step = this.isLicenseRequired ? 0 : 1
    },
  },
  created() {
    this.sentry = useImageSentry({
      imageId: this.localContent?.topicId || 'unknown',
      imageType: ImageType.STOCK,
      topicId: this.localContent?.topicId,
    })
  },
  beforeUnmount() {
    this._pollCancelled = true
    clearTimeout(this._pollTimerId)
    clearTimeout(this._hintTimerId)
  },
  methods: {
    async goToFirstStep() {
      this.resetState(false)
    },
    resetState(resetStep = true) {
      // Preserve isScheduledPost when rescheduling
      if (!this.isUpdateForScheduledPost) {
        this.isScheduledPost = false
      }
      // Preserve isScheduledPost when sharing saved content (it might have been scheduled before)
      if (this.isUpdateForSavedContent && this.localContent.scheduledAt) {
        this.isScheduledPost = true
      }
      if (resetStep) this.selectedPages = []
      this.posts = []
      this.scheduleData = null
      this.postingStatus = null
      this.postingBatchId = null
      this.masterBatchSummary = null

      if (this.isNewsletterTextKind) {
        this.step = 1
      } else {
        this.step = this.isUpdateForScheduledPost ? 2 : this.isLicenseRequired ? 0 : 1
      }
    },
    handleImageLicense(url) {
      this.localContent.image = url
      this.step = 1
    },
    handleUpdatePostSuccess(scheduledPosts) {
      this.posts = scheduledPosts
      this.step = 3
      this.$emit('savedScheduledContent')
    },
    async handleSharingStepNext() {
      this.step = 2
    },
    handleScheduleSelected(scheduleData) {
      this.scheduleData = scheduleData
      this.isScheduledPost = scheduleData.isScheduledPost

      if (this.isMasterView) {
        this.step = 3
      } else {
        this.validateAndPost()
      }
    },
    handlePreviewSubmit() {
      this.validateAndPost()
    },
    validateAndPost() {
      const missingOffices = getOfficesWithMissingPlaceholders(
        this.localContent.text,
        this.getOfficesForPlaceholderValidation()
      )

      if (missingOffices.length > 0) {
        this.placeholderWarningOffices = missingOffices
        this.showPlaceholderWarning = true
        return
      }

      this.postToSocialMedia()
    },
    confirmPlaceholderWarning() {
      this.showPlaceholderWarning = false
      this.postToSocialMedia()
    },
    getOfficesForPlaceholderValidation() {
      if (!this.isMasterView) {
        if (this.company) return [this.company]

        if (this.companyBaseData?.id) {
          return [
            {
              id: this.companyBaseData.id,
              name: this.companyBaseData.name,
              address: {},
            },
          ]
        }

        return []
      }

      const companyIds = []
      const postToAllCompanies = this.publication === 'all'
      const postToMaster =
        this.publication === 'head' || (this.publication === 'individual' && this.includeHeadOffice)

      if (postToMaster) {
        companyIds.push(this.$auth.user.companyId)
      }

      if (this.selectedOffices.length > 0) {
        companyIds.push(...this.selectedOffices.map((office) => office.id))
      }

      if (postToAllCompanies) {
        companyIds.push(...(this.getCompaniesPostingInfo || []).map((company) => company.id))
      }

      const uniqueIds = [...new Set(companyIds.filter(Boolean))]

      const excludedCompanyIdsFromPosting =
        postToAllCompanies && this.isMasterView ? this.excludedCompanyIdsFromPosting || [] : []

      return uniqueIds
        .filter((companyId) => !excludedCompanyIdsFromPosting.includes(companyId?.toString()))
        .map((companyId) => {
          if (companyId === this.$auth.user.companyId && this.company) {
            return this.company
          }

          return this.getCompaniesPostingInfo?.find((company) => company.id === companyId)
        })
        .filter(Boolean)
    },
    platformLabel(platform) {
      const label = ReadableSocialMedia[platform]
      if (label) return label
      // A platform the frontend does not know about yet — show a neutral label
      // rather than leaking the raw enum into the UI.
      Sentry.captureMessage(`Unmapped social media platform: ${platform}`, 'warning')
      return this.$t('social-media.posting.unknown-platform')
    },
    async postToSocialMedia() {
      this.isLoading = true
      try {
        const processedImages = await this.processImages()
        const processedCarouselImages = await this.processCarouselImages()

        const targetLinkOption = this.mapTargetLinkToOption(this.targetLink)
        const customLink =
          this.targetLink === TargetLink.individualLink ? this.individualLink : undefined

        const companyIds =
          this.selectedOffices && this.selectedOffices.length > 0
            ? this.selectedOffices.map((office) => office.id)
            : [this.$auth.user.companyId]

        const postToAllCompanies = this.isMasterView && this.publication === 'all'

        // Derived from the same connectivity value the preview summary renders,
        // so an office can never be posted to while being listed as not
        // connected (or vice versa).
        const onlyConnectedCompanies = () => {
          if (postToAllCompanies) return []
          return companyIds.filter((companyId) =>
            this.selectedPages.some(
              (page) => this.getSocialMediaConnection(companyId, page.type)?.isConnected
            )
          )
        }

        const postToMaster = !this.isMasterView
          ? true
          : this.publication === 'head' ||
            (this.publication === 'individual' && this.includeHeadOffice)

        const headOfficeId = this.$auth.user.companyId

        const connectedCompanyIds = this.isMasterView ? onlyConnectedCompanies() : []
        let companyIdsToPost = this.isMasterView ? [...connectedCompanyIds] : [headOfficeId]

        // Selected offices without an active connection for the chosen platforms.
        // The backend records these so both the History details and the success
        // modal can show every selected office (as "not connected"), rather than
        // silently dropping them. Post-to-all offices are derived server-side.
        let notConnectedCompanyIds =
          this.isMasterView && !postToAllCompanies
            ? [
                ...new Set(
                  companyIds.filter((companyId) => !connectedCompanyIds.includes(companyId))
                ),
              ]
            : []

        if (postToMaster) {
          companyIdsToPost = companyIdsToPost.filter((companyId) => companyId !== headOfficeId)
          notConnectedCompanyIds = notConnectedCompanyIds.filter(
            (companyId) => companyId !== headOfficeId
          )
        }

        const input = {
          companyIds: companyIdsToPost,
          ...(notConnectedCompanyIds.length > 0 && { notConnectedCompanyIds }),
          message: this.localContent.text,
          imageLink: this.localContent.image,
          platforms: this.selectedPages.map((page) => page.type),
          imageSource: this.localContent.imageSource,
          textKind: this.localContent.textKind,
          topicId: this.localContent.topicId,
          targetLinkOption,
          ...(customLink && { customLink }),
          isMasterView: this.isMasterView,
          postToAllCompanies,
          ...(this.localContent.modelName === 'AI-gemini-flash-personalized' &&
            this.isMasterView && {
              modelName: 'gemini-flash-personalized',
            }),
          postToMaster,
          ...(this.localContent.isVideo && {
            isVideo: true,
            mediaType: this.localContent.mediaType,
          }),
          ...(this.scheduleData?.isScheduledPost && {
            ...(this.scheduleData.customSchedules?.length
              ? { customSchedules: this.scheduleData.customSchedules }
              : { scheduledAt: this.scheduleData.date }),
          }),
          ...(processedCarouselImages && processedCarouselImages.length > 0
            ? { carouselImages: processedCarouselImages }
            : this.localContent.carouselImages?.length > 0
              ? { carouselImages: this.localContent.carouselImages }
              : {}),
          ...(processedImages && processedImages.length > 0 && { images: processedImages }),
        }

        const { data } = await this.$apollo.mutate({
          mutation: POST_TO_SOCIAL_MEDIA_FOR_COMPANIES,
          variables: { input },
        })

        const result = data?.result
        const postingOperationId = result?.postingOperationId
        this.postingBatchId = result?.postingBatchId || postingOperationId

        this.$tracking.event('Content Creator', 'Clicked', 'Share', this.localContent.textKind)

        if (this.isScheduledPost || !postingOperationId) {
          const mockPosts = this.isScheduledPost
            ? [
                {
                  scheduledAt: this.scheduleData?.date,
                  platforms: this.selectedPages.map((p) => p.type),
                },
              ]
            : [{ platforms: this.selectedPages.map((p) => p.type) }]
          await this.fetchMasterBatchSummary()
          // mockPosts drives the UI (it's just "what we asked for"); tracking
          // must use the mutation's own posts field, which reflects what the
          // backend actually confirmed per platform.
          this.handleSuccess(mockPosts, result?.posts)
          return
        }

        this.isLoading = false
        this.isPolling = true
        this._pollCancelled = false
        this._hintTimerId = setTimeout(() => {
          this.showHistoryHint = true
        }, 60000)
        await this.pollPostingStatus(postingOperationId)
      } catch (error) {
        showSnackbarMessage('error', this.$t('social-media.alert.something-went-wrong'))
      } finally {
        this.isLoading = false
        this.isPolling = false
        this.showHistoryHint = false
        clearTimeout(this._hintTimerId)
      }
    },

    dismissPollingAndClose() {
      this._pollCancelled = true
      clearTimeout(this._pollTimerId)
      clearTimeout(this._hintTimerId)
      this.isPolling = false
      this.showHistoryHint = false
      this.dialog = false
    },
    async fetchMasterBatchSummary() {
      // Derive the success-modal counts from the same source and status logic as
      // the History details (getMasterBatchPosts), so the two always agree. Each
      // office × platform is one publishing target counted as successful, failed
      // or not-connected.
      if (!this.isMasterView || !this.postingBatchId) return
      try {
        const { data } = await this.$apollo.query({
          query: GET_MASTER_BATCH_POSTS,
          variables: { input: { postingBatchId: this.postingBatchId } },
          fetchPolicy: 'no-cache',
        })
        this.masterBatchSummary = summarizeBatchPosts(
          data?.getMasterBatchPosts || [],
          this.getCompaniesPostingInfo || [],
          this.contentType
        )
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'SocialMediaSharing', action: 'fetchMasterBatchSummary' },
          extra: { postingBatchId: this.postingBatchId },
        })
        this.masterBatchSummary = null
      }
    },
    async pollPostingStatus(postingOperationId) {
      const POLL_INTERVAL_MS = 2000
      const TIMEOUT_MS = 600000
      const startTime = Date.now()

      return new Promise((resolve) => {
        const poll = async () => {
          if (this._pollCancelled) {
            resolve()
            return
          }

          try {
            if (Date.now() - startTime > TIMEOUT_MS) {
              showSnackbarMessage('info', this.$t('social-media.posting.timeout'))
              resolve()
              return
            }

            const { data } = await this.$apollo.query({
              query: SOCIAL_MEDIA_POSTING_STATUS,
              variables: { postingOperationId },
              fetchPolicy: 'no-cache',
            })

            if (this._pollCancelled) {
              resolve()
              return
            }

            const status = data?.result
            if (status) {
              this.postingStatus = status
            }

            if (status?.status === 'COMPLETED' || status?.status === 'FAILED') {
              const posts =
                status.platforms.length > 0
                  ? status.platforms.map((p) => ({
                      hasFailed: p.hasFailed,
                      type: p.platform,
                      permalink: p.permalink,
                      externalPostId: null,
                    }))
                  : [{ platforms: this.selectedPages.map((p) => p.type) }]
              await this.fetchMasterBatchSummary()
              this.handleSuccess(posts)
              resolve()
              return
            }

            this._pollTimerId = setTimeout(poll, POLL_INTERVAL_MS)
          } catch (error) {
            if (!this._pollCancelled) {
              showSnackbarMessage('error', this.$t('social-media.alert.something-went-wrong'))
            }
            resolve()
          }
        }

        poll()
      })
    },
    mapTargetLinkToOption(targetLink) {
      const mapping = {
        [TargetLink.noLink]: 'NO_LINK',
        [TargetLink.customerMainWebsite]: 'CUSTOMER_MAIN_WEBSITE',
        [TargetLink.officeWebsite]: 'OFFICE_WEBSITE',
        [TargetLink.landingpagePerOffice]: 'OFFICE_BOTTIMMO_LANDINGPAGE',
        [TargetLink.landingpage]: 'CENTRAL_BOTTIMMO_LANDINGPAGE',
        [TargetLink.googleReview]: 'GOOGLE_REVIEW',
        [TargetLink.individualLink]: 'CUSTOM',
      }
      return mapping[targetLink] || 'NO_LINK'
    },
    getAspectRatioForImage() {
      const textKind = this.localContent.textKind
      return textKind && ratioMap[textKind] ? ratioMap[textKind] : 1
    },
    async processStockImageWithCropping(image) {
      if (!image || image.provider !== 'STOCK') {
        return image
      }

      if (image.fileId || (image.history && image.history.length > 0)) {
        this.sentry.addBreadcrumb(
          'Skipping already edited stock image',
          {
            imageId: image.id,
            fileId: image.fileId,
            hasHistory: image.history?.length > 0,
          },
          'info'
        )
        return image
      }

      try {
        const { data: prepareData } = await this.$apollo.mutate({
          mutation: PREPARE_STOCK_IMAGE_FOR_EDIT,
          variables: {
            input: {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              topicId: image.topicId,
            },
          },
        })

        if (prepareData?.prepareStockImageForEdit?.isAlreadyLicensed) {
          this.sentry.addBreadcrumb(
            'Skipping already licensed stock image',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
            },
            'info'
          )
          return image
        }

        if (!prepareData?.prepareStockImageForEdit?.canEdit) {
          this.sentry.captureImageWarning(
            'Stock image cannot be edited',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              prepareResponse: prepareData?.prepareStockImageForEdit,
            },
            'SocialMediaSharing'
          )
          return image
        }

        const aspectRatio = this.getAspectRatioForImage()
        const processedImage = await cropImageToAspectRatio(
          prepareData.prepareStockImageForEdit.originalImageUrl,
          aspectRatio,
          this.sentry,
          'SocialMediaSharing'
        )

        if (!processedImage) {
          this.sentry.captureImageWarning(
            'Failed to process stock image',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              originalImageUrl: prepareData.prepareStockImageForEdit.originalImageUrl,
              aspectRatio,
              textKind: this.localContent.textKind,
            },
            'SocialMediaSharing'
          )
          return image
        }

        const { data: editData } = await this.$apollo.mutate({
          mutation: EDIT_IMAGE,
          variables: {
            input: {
              imageId: image.id,
              image: processedImage,
              imageType: ImageType.STOCK,
              topicId: image.topicId,
              provider: image.provider,
              externalId: image.externalId,
            },
          },
        })

        return editData?.editImage || image
      } catch (error) {
        this.sentry.captureImageError(
          error,
          'processStockImageWithCropping',
          {
            imageId: image.id,
            provider: image.provider,
            externalId: image.externalId,
            textKind: this.localContent.textKind,
          },
          'SocialMediaSharing'
        )
        return image
      }
    },
    async processImages() {
      if (!this.localContent.images?.length) return []

      return Promise.all(
        this.localContent.images.map(async (image) => {
          try {
            return await this.processStockImageWithCropping(image)
          } catch (error) {
            showSnackbarMessage('error', this.$t('alerts.image-edit.error-processing'))
            throw error
          }
        })
      )
    },
    async processCarouselImages() {
      if (!this.localContent.carouselImages?.length) return []
      return this.localContent.carouselImages
    },
    // `confirmedPosts` defaults to `posts` for the polling path, where `posts`
    // already is the backend-confirmed per-platform result. The immediate/
    // synchronous path passes its own confirmed posts separately, since
    // `posts` there is a synthetic UI-only placeholder.
    handleSuccess(posts, confirmedPosts = posts) {
      this.posts = posts
      this.step = this.isMasterView ? 4 : 3

      if (posts && posts.length > 0) {
        if (!posts.every(({ hasFailed }) => hasFailed)) {
          if (this.isScheduledPost) {
            this.$emit('savedScheduledContent')
          } else {
            if (confirmedPosts) {
              this.trackPublishedChannels(confirmedPosts)
            }
            this.$emit('contentSaved')
          }
        }
      } else {
        Sentry.captureMessage(
          'SocialMediaSharing - handleSuccess received empty or invalid posts array',
          {
            level: 'error',
          }
        )
      }
    },
    // Only called for confirmed, immediate publishes (not scheduled posts, and
    // not on click) — one heap event per channel actually posted to.
    trackPublishedChannels(posts) {
      posts
        .filter((post) => !post.hasFailed)
        .flatMap((post) => (post.type ? [post.type] : post.platforms || []))
        .forEach((channel) => this.$tracking.heapEvent('post_published', { channel }))
    },
    getTargetLinkUrl(companyId) {
      if (!this.getCompaniesPostingInfo) return ''
      if (this.targetLink === TargetLink.noLink) return ''
      else if (this.targetLink === TargetLink.individualLink) return this.content.targetLink.custom
      else if (this.targetLink === TargetLink.officeWebsite)
        return (
          this.getCompaniesPostingInfo.find((company) => company.id === companyId)?.website || ''
        )
      else if (this.targetLink === TargetLink.landingpagePerOffice)
        return (
          this.getCompaniesPostingInfo.find((company) => company.id === companyId)
            ?.landingpageUrl || ''
        )
      else if (this.targetLink === TargetLink.landingpage) {
        const masterCompanyData = this.getCompaniesPostingInfo.find(
          (company) => company.id === this.$auth.user.companyId
        )
        return masterCompanyData?.landingpageUrl || ''
      } else if (this.targetLink === TargetLink.customerMainWebsite) {
        const masterCompanyData = this.getCompaniesPostingInfo.find(
          (company) => company.id === this.$auth.user.companyId
        )
        return masterCompanyData?.website || ''
      } else return ''
    },
    getSocialMediaConnection(companyId, platformType = null) {
      const postingFor =
        platformType ||
        (this.selectedPages && this.selectedPages.length > 0 ? this.selectedPages[0]?.type : null)
      if (!postingFor) {
        return null
      }

      const type = String(postingFor).toUpperCase()
      const connected = (username) => ({
        platform: type,
        username,
        isConnected: true,
        statusLabel: null,
      })
      const notConnected = (statusLabel = null) => ({
        platform: type,
        username: null,
        isConnected: false,
        statusLabel,
      })

      const postingInfo = this.getCompaniesPostingInfo?.find((company) => company.id === companyId)
      const connections = postingInfo?.connectedSocialMediaTypes
      if (!connections) {
        return notConnected()
      }

      if (type === SocialMedia.FACEBOOK) {
        const facebookPages = Array.isArray(connections.facebook) ? connections.facebook : []
        const active = facebookPages.find((facebook) => facebook?.isActive)
        return active?.name ? connected(active.name) : notConnected()
      } else if (type === SocialMedia.INSTAGRAM) {
        const facebookPages = Array.isArray(connections.facebook) ? connections.facebook : []
        const active = facebookPages.find(
          (facebook) => facebook?.isActive
        )?.instagram_business_account
        return active?.username ? connected(active.username) : notConnected()
      } else if (type === SocialMedia.LINKEDIN) {
        const linkedinPages = Array.isArray(connections.linkedin) ? connections.linkedin : []
        const active = linkedinPages.find((linkedin) => linkedin?.isActive)
        return active?.name ? connected(active.name) : notConnected()
      } else if (type === SocialMedia.GOOGLE_BUSINESS) {
        const googleBusiness = connections.googleBusiness
        if (googleBusiness?.status === GoogleBusinessConnectionStatus.NEEDS_LOCATION) {
          return notConnected(this.$t('content-creator.google-business.needs-location'))
        }
        if (googleBusiness?.status !== GoogleBusinessConnectionStatus.CONNECTED) {
          return notConnected()
        }
        return connected(
          googleBusiness.locationName || this.platformLabel(SocialMedia.GOOGLE_BUSINESS)
        )
      }

      return notConnected()
    },
    toPreviewAccount(companyId, platformType) {
      const connection = this.getSocialMediaConnection(companyId, platformType)
      return {
        platform: platformType,
        username: connection?.username || null,
        isConnected: Boolean(connection?.isConnected),
        statusLabel: connection?.statusLabel || null,
      }
    },
  },
  apollo: {
    childCompanies: {
      query: GET_CHILD_COMPANIES,
      skip() {
        return !this.isMasterView
      },
    },
    getCompaniesPostingInfo: {
      query: GET_COMPANIES_POSTING_INFO,
      variables() {
        return {
          input: {
            parentId: this.$auth?.user?.companyId || '',
            topicId: this.localContent?.topicId || null,
          },
        }
      },
      // topicId only resolves the landing page URL; the connection data this query
      // also carries must load without it, or every office reads as not connected.
      skip() {
        return (
          !this.isMasterView ||
          this.$auth.loading ||
          !this.$auth.isAuthenticated ||
          !this.$auth?.user?.companyId
        )
      },
      update(data) {
        return data?.getCompaniesPostingInfo || []
      },
      error(error) {
        console.error('Failed to fetch companies posting info:', error)
        Sentry.captureException(error, {
          tags: {
            component: 'SocialMediaSharing',
            action: 'getCompaniesPostingInfo',
          },
          extra: {
            topicId: this.localContent?.topicId,
            companyId: this.$auth?.user?.companyId,
          },
        })
      },
    },
  },
  emits: ['savedScheduledContent', 'contentSaved'],
}
</script>

<style>
.dialog-title {
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  flex: unset;
}

.social-media-dialog :deep(.v-stepper__header) {
  box-shadow: none;
}

.social-media-dialog :deep(.v-stepper-window) {
  overflow-y: auto;
}

@media (max-width: 600px) {
  .button {
    width: 47%;
  }

  .social-media-dialog {
    border-radius: 0 !important;
    overflow-y: hidden;
    height: 100%;
    width: 100%;

    :deep(.v-stepper),
    :deep(.v-stepper-window),
    :deep(.v-window-item) {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    :deep(.v-stepper-window) {
      flex: 1;
      overflow-y: auto;
    }
  }
}
</style>
